package app.nestbridge.feature.invitations.service

import app.nestbridge.feature.invitations.api.InvitationApiClient
import app.nestbridge.feature.invitations.model.Invitation
import me.tatarka.inject.annotations.Inject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope
import software.amazon.lastmile.kotlin.inject.anvil.ContributesBinding
import software.amazon.lastmile.kotlin.inject.anvil.SingleIn

@SingleIn(AppScope::class)
@ContributesBinding(AppScope::class, boundType = InvitationService::class)
@Inject
class DefaultInvitationService(
    private val apiClient: InvitationApiClient,
    private val teamMembershipUpdater: TeamMembershipUpdater,
) : InvitationService {

    override suspend fun findSentInvitations(): Result<List<Invitation>> =
        runCatching { apiClient.fetchSentInvitations().map { it.toDomain() } }

    override suspend fun findReceivedInvitations(): Result<List<Invitation>> =
        runCatching { apiClient.fetchReceivedInvitations().map { it.toDomain() } }

    override suspend fun findById(invitationId: String): Result<Invitation?> =
        runCatching { apiClient.fetchById(invitationId)?.toDomain() }

    override suspend fun findByClaimToken(token: String): Result<Invitation?> =
        runCatching { apiClient.fetchByClaimToken(token)?.toDomain() }

    override suspend fun getPendingTeamCreationInvitation(childId: String): Result<Invitation?> =
        runCatching { apiClient.fetchPendingTeamCreationInvitation(childId)?.toDomain() }

    override suspend fun create(invitation: Invitation): Result<Invitation> =
        runCatching { apiClient.create(invitation.toDto()).toDomain() }

    override suspend fun initiateVerification(invitationId: String): Result<Unit> =
        runCatching { apiClient.initiateVerification(invitationId) }

    override suspend fun accept(invitationId: String): Result<Unit> =
        runCatching { apiClient.accept(invitationId) }.fold(
            onSuccess = { updateCareTeamMembershipForInvitation(invitationId) },
            onFailure = { Result.failure(it) },
        )

    override suspend fun accept(invitationId: String, verificationCode: String): Result<Unit> =
        apiClient.accept(invitationId, verificationCode).fold(
            onSuccess = { updateCareTeamMembershipForInvitation(invitationId) },
            onFailure = { Result.failure(it) },
        )

    override suspend fun decline(invitationId: String): Result<Unit> =
        runCatching { apiClient.decline(invitationId) }

    override suspend fun rescind(invitationId: String): Result<Unit> =
        runCatching { apiClient.rescind(invitationId) }

    private suspend fun updateCareTeamMembershipForInvitation(invitationId: String): Result<Unit> =
        runCatching {
            val invitation = apiClient.fetchById(invitationId)?.toDomain() ?: return@runCatching
            val teamId = invitation.teamId ?: return@runCatching
            teamMembershipUpdater
                .addCurrentUserIfMissing(teamId = teamId, role = invitation.proposedTeamRole)
                .getOrThrow()
        }
}
